import React, { useState } from 'react';
import { 
  FiChevronLeft, 
  FiChevronRight, 
  FiUsers,
  FiUser,
  FiCalendar,
  FiSearch,
  FiLink,
  FiEye
} from 'react-icons/fi';

/**
 * ReferralUsers Component
 * Displays paginated list of all referral users with their info
 */
const ReferralUsers = ({ 
  users = [], 
  totalCount = 0, 
  page = 1, 
  limit = 10, 
  onPageChange, 
  onViewChain,
  loading 
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const totalPages = Math.ceil(totalCount / limit);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      u.username?.toLowerCase().includes(term) ||
      u.user_id?.toLowerCase().includes(term) ||
      u.onboarding_referrer_username?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-500 text-white p-2 rounded-lg">
              <FiUsers className="text-lg" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Referral Users</h3>
              <p className="text-sm text-gray-500">{totalCount} total users</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-auto"
            />
          </div>
        </div>
      </div>

      {/* Users Grid */}
      <div className="p-4">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FiUsers className="mx-auto text-3xl mb-2 text-gray-300" />
            <p>No users found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredUsers.map((user, index) => (
              <div
                key={user.user_id || index}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-gradient-to-br from-white to-gray-50"
              >
                {/* User Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-indigo-100 p-2 rounded-full">
                      <FiUser className="text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">@{user.username}</p>
                      <p className="text-xs text-gray-400 truncate max-w-[150px]" title={user.user_id}>
                        {user.user_id}
                      </p>
                    </div>
                  </div>
                  
                  {onViewChain && (
                    <button
                      onClick={() => onViewChain(user.user_id, user.username)}
                      className="p-2 hover:bg-blue-50 rounded-lg text-blue-500 transition-colors"
                      title="View Referral Chain"
                    >
                      <FiEye />
                    </button>
                  )}
                </div>

                {/* Referrer Info */}
                <div className="space-y-2">
                  {user.onboarding_referrer_username ? (
                    <div className="flex items-center gap-2 text-sm">
                      <FiLink className="text-green-500" />
                      <span className="text-gray-600">
                        Referred by: <span className="font-medium text-green-600">@{user.onboarding_referrer_username}</span>
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <FiLink />
                      <span>No referrer</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <FiCalendar className="text-gray-400" />
                    <span>Joined: {formatDate(user.created_at)}</span>
                  </div>
                </div>

                {/* Referral Code Badge */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Referral Code</span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-mono">
                      {user.username}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiChevronLeft />
            </button>
            
            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium ${
                      page === pageNum
                        ? 'bg-blue-500 text-white'
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page === totalPages}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralUsers;
